import React, { useState } from 'react';
import Fuse from 'fuse.js';

import type { StoredNotification } from '../../../model/StoredNotification';
import type { ButtonOptions } from '../../../../client';
import type {
  TemplateMarkdown,
  TemplateCustom,
  SectionAlignment,
} from '../../../../client/templates/templates';
import { TemplateComponentMap } from '../Templates';
import { isStoredNotification } from '../../../utils';
import { IndicatorBanner, InfoBanner } from '../IndicatorBanner/IndicatorBanner';
import { ButtonsContainer } from '../CardButton/CardButtonContainer';

import { CardHeader } from './CardHeader';
import { Card } from './Card';
import { Box } from '@openfin/ui-library';
import styled, { css } from 'styled-components';
import { SingleLine } from '../SingleLine/SingleLine';
import { isTemplate } from '../../../model/templates/isTemplate';
import { useIsStateUpdate } from '../../hooks';

export interface NotificationCardComponentProps {
  /**
   * The notification data to display in a card.
   * If the notification is wrapped in search data the notification will be
   * displayed with additional features such as highlighting.
   */
  notification: StoredNotification | Fuse.FuseResult<StoredNotification>;
  isToast?: boolean;
  stackSize?: number;
  style?: React.CSSProperties;
  tabIndex?: number;
  /**
   * Used to notify of update to Notification
   */
  revisionNumber?: number;
  /**
   * Override the sender title of a notification
   */
  senderTitle?: string;

  remove?: (note: StoredNotification) => void;
  minimize?: (note: StoredNotification) => void;
  clickAction?: (note: StoredNotification) => void;
  clickButtonAction?: (note: StoredNotification, buttonIndex: number) => void;
  submitForm?: (note: StoredNotification, form: Record<string, unknown>) => void;
}

export const NotificationCardComponent: React.FC<NotificationCardComponentProps> = (props) => {
  const {
    stackSize,
    style,
    senderTitle,
    isToast = false,
    clickAction,
    remove,
    minimize,
    submitForm,
    clickButtonAction,
    tabIndex,
    revisionNumber,
  } = props;

  const notification = isStoredNotification(props.notification)
    ? props.notification
    : props.notification.item;
  const data = notification.notification;
  const title = senderTitle || notification.title;
  const buttonAlignment = isTemplate<TemplateCustom>(data, 'custom')
    ? data.templateOptions.buttons?.align
    : undefined;
  const indicatorAlignment: SectionAlignment = isTemplate<TemplateCustom>(data, 'custom')
    ? data.templateOptions.indicator?.align || 'center'
    : 'center';
  const indicator = isTemplate<TemplateCustom>(data, 'custom')
    ? {
        ...data.indicator,
        color: data.indicator?.color ?? data.templateOptions.indicator?.color,
      }
    : data.indicator;

  const handleDismiss = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (!showOverlay) {
      if (isToast) {
        minimize?.(notification);
      } else {
        remove?.(notification);
      }
    }
  };

  const handleButtonClick = async (event: React.MouseEvent, button: ButtonOptions) => {
    if (!button.submit) {
      event.stopPropagation();
      event.preventDefault();
      const buttonIndex = button.index || 0;
      await clickButtonAction?.(notification, buttonIndex);
    }
  };

  const handleNotificationClick = async (event: React.MouseEvent) => {
    if (isTemplate<TemplateMarkdown>(data, 'markdown') && data.form) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    if (!showOverlay) {
      await clickAction?.(notification);
    }
  };

  const Template = TemplateComponentMap[notification.notification.template];

  if (!Template) {
    console.error(
      `Unable to render ${notification.id}. Unknown template ${notification.notification.template}.`,
      notification
    );
    return null;
  }

  const [isHovered, setIsHovered] = useState(false);

  /**
   * if revisionNumber changes it's an update (and the user is hovering the card) then
   * show the overlay
   */
  const [showOverlay, resetOverlay] = useIsStateUpdate(
    revisionNumber,
    (prev, update) => isHovered && prev !== update
  );

  const handleOverlayConfirmButtonClick = () => resetOverlay();

  const UpdatedNotificationButtonOptions: ReadonlyArray<Required<ButtonOptions>> = [
    {
      title: 'OK',
      cta: true,
      index: 0,
      iconUrl: '',
      submit: false,
      type: 'button',
      onClick: handleOverlayConfirmButtonClick,
    },
  ];
  const buttons =
    showOverlay && data.buttons.length > 0 ? UpdatedNotificationButtonOptions : data.buttons;

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    resetOverlay();
  };

  const cardContent = (
    <>
      <CardHeader
        sender={title}
        icon={data.icon}
        uuid={data.stream?.id ?? JSON.stringify(notification.source)}
        date={data.date}
        onDismiss={handleDismiss}
        data-testid="header"
      />
      {data.title && (
        <SingleLine weight="bold" data-testid="notification-title">
          {data.title}
        </SingleLine>
      )}
      <TemplateContainer flexDirection="column" data-testid="template-content">
        <Template
          // @ts-ignore Need to cast `notification` to `StoredNotification<T>`
          notification={notification}
          submitForm={submitForm}
          clickButtonAction={clickButtonAction}
        />
      </TemplateContainer>
    </>
  );
  return (
    <Card
      aria-label="Notification"
      onClick={handleNotificationClick}
      data-testid="notification-card"
      data-id={notification.id}
      style={style}
      hasButtons={buttons.length > 0}
      tabIndex={tabIndex}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {indicator && (
        <IndicatorContainer alignment={indicatorAlignment}>
          {showOverlay ? (
            <InfoBanner text="Notification Updated" />
          ) : (
            <IndicatorBanner
              indicator={indicator}
              stackSize={stackSize}
              alignment={indicatorAlignment}
            />
          )}
        </IndicatorContainer>
      )}
      <CardContent flexDirection="column" gap="xsmall">
        {showOverlay ? (
          <UpdateOverlay data-testid="update-overlay" gap="xsmall">
            {cardContent}
          </UpdateOverlay>
        ) : (
          cardContent
        )}
        {buttons.length > 0 &&
          // Display buttons if no form is present
          !(isTemplate<TemplateMarkdown>(data, 'markdown') && data.form) && (
            <ButtonsContainer
              alignment={buttonAlignment}
              buttonData={buttons}
              data-testid="buttons-container"
              onClick={showOverlay ? handleOverlayConfirmButtonClick : handleButtonClick}
            />
          )}
      </CardContent>
    </Card>
  );
};

const IndicatorContainer = styled(Box)<{ alignment: SectionAlignment }>`
  position: absolute;
  ${(props) => {
    switch (props.alignment) {
      case 'center':
        return css`
          top: 0;
          width: 100%;
          & + * {
            margin-top: ${(props) => props.theme.px.xxlarge};
          }
        `;
      case 'left':
        return css`
          left: 0;
          height: 100%;
          & + * {
            margin-left: ${(props) => props.theme.px.xxlarge};
            padding: ${({ theme }) => `0 ${theme.px.base}`};
          }
        `;
      case 'right':
        return css`
          right: 0;
          height: 100%;
          & + * {
            margin-right: ${(props) => props.theme.px.xxlarge};
            padding: ${({ theme }) => `0 ${theme.px.base}`};
          }
        `;
    }
  }};
`;

const UpdateOverlay = styled(Box)`
  width: 100%;
  opacity: 0.5;
  z-index: 99;
  display: flex;
  flex-direction: column;
`;

const CardContent = styled(Box)`
  flex: 1;
  width: 100%;
`;

const TemplateContainer = styled(Box)`
  position: relative;
  flex: 1;
  max-height: 400px;
  overflow-x: hidden;
  overflow-y: overlay;
`;
